<template>
  <div v-if="application" class="application-header rounded-lg p-4">
    <span class="text-subtle">Ansökningsnummer: {{ application.publicId }}</span>
    <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div>
        <div class="flex flex-wrap items-center gap-2">
          <h2>{{ headerTitle }}</h2>
          <AppChip v-if="application.type !== 'commitmentApplication' && application.isHighPrio" color="warning">
            <SvgIcon :path="mdiArrowUpThick" size="16" />
            Hög prio
          </AppChip>
        </div>
        <p v-if="application.type !== 'commitmentApplication' && application.collateralProperty" class="text-subtle">
          {{ formatAddress(application.collateralProperty?.address) }},
          {{ `${application.collateralProperty.address.zip ?? ''} ${application.collateralProperty.address.city}` }}
        </p>
      </div>

      <div class="flex flex-col items-end gap-2 text-xs text-subtle">
        <span>Handläggare: {{ getPersonnelNameByPersonnelId(application.brokerExternalId) }}</span>
        <span v-if="application.createdAt">Skapad: {{ formatDateTimeValue(application.createdAt) }}</span>
        <span v-if="application.updatedAt">Senast uppdaterad: {{ formatDateTimeValue(application.updatedAt) }}</span>
        <span v-if="application.submittedAt">Inkom: {{ formatDateTimeValue(application.submittedAt) }}</span>
        <WorkResponsibilityControl v-if="!isCommitment" />
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-6">
      <div class="shrink-0">
        <p>
          Status: <span class="font-medium">{{ t(`APPLICATION_STATUS.${application.status}`) }}</span>
        </p>
        <p>
          Ansökt belopp: <span class="font-medium">{{ formatAmountWithCurrency(application.amountAppliedFor) }}</span>
        </p>
      </div>

      <div class="flex flex-wrap gap-4">
        <RouterLink
          v-for="applicant in applicantInfo"
          :key="applicant.id"
          class="applicant-box rounded-md px-4 py-3"
          :to="`/kunder/${applicant.id}`"
        >
          <span class="text-xs text-subtle">
            {{ applicant.role }}
          </span>
          <div class="font-medium capitalize whitespace-nowrap">{{ applicant.name.toLowerCase() }}</div>
        </RouterLink>
      </div>

      <div class="ml-auto flex flex-wrap gap-2">
        <AppButton v-if="!isCommitment" color="blue" :disabled="isStatusActive" :loading="isCreatingAgreements" @click="handleCreateLoanAgreements">Skapa lånehandlingar <template #append> <SvgIcon :path="mdiFileDocumentOutline" size="16" /> </template
        ></AppButton>
        <OpenConversationsButton :application-id="applicationId" />
        <AppButton v-if="!isCommitment" color="blue" :disabled="isStatusActive" @click="openCancelModal"
          >Makulera <template #append> <SvgIcon :path="mdiCancel" size="16" /> </template
        ></AppButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { mdiArrowUpThick, mdiCancel, mdiFileDocumentOutline } from '@mdi/js'

import { formatAddress } from '@/utils/formatAddress'
import { getFullName, isCommitmentApplication } from '@/utils/applicationDisplay'
import { ApiRequestError, createLoanAgreements } from '@/services/application'

import { formatAmountWithCurrency, formatDateTimeValue } from '@staff/shared'
import { AppButton, AppChip, useModal, useNotifications, SvgIcon } from '@staff/ui'

import { usePersonnelDirectory } from '@/composables/usePersonnelDirectory'

import OpenConversationsButton from '@/components/OpenConversationsButton.vue'
import { useApplicationRecord } from './application.context'
import WorkResponsibilityControl from './WorkResponsibilityControl.vue'
import CancelApplicationModalContent from './CancelApplicationModalContent.vue'

const props = defineProps<{
  applicationId: string
}>()

const { application } = useApplicationRecord()
const refreshApplication = inject<() => Promise<void>>('refreshApplication')
const { openModal } = useModal()
const { notify } = useNotifications()

const { t } = useI18n()
const { getPersonnelNameByPersonnelId } = usePersonnelDirectory()

const isCommitment = computed(() => Boolean(application.value && isCommitmentApplication(application.value.type)))
const isStatusActive = computed(() => application.value?.status === 'active')

const headerTitle = computed(() => {
  const app = application.value
  if (!app) return ''

  const typeLabel = t(`APPLICATION_TYPE.${app.type}`)

  if (app.type === 'commitmentApplication') {
    return typeLabel
  }

  if (!app.collateralProperty) {
    return typeLabel
  }

  return `${typeLabel} - ${t(`PROPERTY_TYPE.${app.collateralProperty.type}`)}`
})

const applicantInfo = computed(() => {
  return (application.value?.applicants ?? []).map((a) => ({
    id: a.userId,
    role: a.roles.isMainApplicant ? 'Huvudlåntagare' : 'Medlåntagare',
    name: getFullName(a),
  }))
})

const isCreatingAgreements = ref(false)

const handleCreateLoanAgreements = async () => {
  if (isCreatingAgreements.value) return
  isCreatingAgreements.value = true
  try {
    await createLoanAgreements(props.applicationId)
    await refreshApplication?.()
    notify({ type: 'success', title: 'Lånehandlingar har skapats' })
  } catch (err) {
    const title = err instanceof Error ? err.message : 'Kunde inte skapa lånehandlingar'
    const description = err instanceof ApiRequestError && err.details.length ? err.details.join('; ') : undefined
    notify({ type: 'error', title, description, dismissable: true })
  } finally {
    isCreatingAgreements.value = false
  }
}

const openCancelModal = () => {
  openModal('Makulera ansökan', CancelApplicationModalContent, {
    applicationId: props.applicationId,
    onSuccess: async () => {
      await refreshApplication?.()
      notify({ type: 'success', title: 'Ansökan har makulerats' })
    },
  })
}
</script>

<style scoped lang="scss">
.application-header {
  background-color: var(--color-brand-subtle);
}

.applicant-box {
  border: 1px solid var(--color-border-divider);
  background-color: var(--color-canvas-translucent);
}
</style>
